/**
 * @fileoverview Sort component for sorting items by different criteria
 * @module ctl-sort
 * @version 1.0.0
 * 
 * @description
 * A sort dropdown component for changing the sort order of items.
 * Supports custom sort options and displays the current selection.
 * 
 * @example
 * <!-- Basic usage -->
 * <ctl-sort></ctl-sort>
 * 
 * @example
 * <!-- With custom options -->
 * <ctl-sort 
 *   options='[
 *     {"label": "Price: Low to High", "value": "price-asc"},
 *     {"label": "Price: High to Low", "value": "price-desc"}
 *   ]'
 * ></ctl-sort>
 */

import { safeJsonParse } from '../../../utils/safeJsonParse.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: var(--ctl-font-sans);
    --ctl-color-text: var(--ctl-color-text);
    --ctl-color-muted: var(--ctl-color-muted);
    --ctl-color-border: var(--ctl-color-border);
    --ctl-color-surface: var(--ctl-color-surface);
    --ctl-color-brand: var(--ctl-color-brand);

    display: inline-block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .container {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2);
  }

  .label {
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-normal);
    color: var(--ctl-color-muted);
    white-space: nowrap;
  }

  .select-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    padding: var(--ctl-space-2) var(--ctl-space-8) var(--ctl-space-2) var(--ctl-space-3);
    border: var(--ctl-space-0-5) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-md);
    background: var(--ctl-color-surface);
    color: var(--ctl-color-text);
    font-family: inherit;
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-normal);
    cursor: pointer;
    transition: border-color var(--ctl-transition-fast) ease, box-shadow var(--ctl-transition-fast) ease;
    min-width: var(--ctl-space-40);
  }

  select:hover {
    border-color: color-mix(in srgb, var(--ctl-color-border) 70%, var(--ctl-color-text));
  }

  select:focus {
    outline: none;
    border-color: var(--ctl-color-brand);
    box-shadow: var(--ctl-focus-ring);
  }

  select:disabled {
    opacity: var(--ctl-opacity-disabled);
    cursor: not-allowed;
  }

  .arrow {
    position: absolute;
    right: var(--ctl-space-3);
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-sm);
  }

  /* Variant: compact */
  :host([variant="compact"]) .label {
    display: none;
  }

  :host([variant="compact"]) select {
    min-width: var(--ctl-space-35);
    padding: var(--ctl-space-1-5) var(--ctl-space-7) var(--ctl-space-1-5) var(--ctl-space-2-5);
    font-size: var(--ctl-font-size-sm);
  }
</style>

<div class="container" part="container">
  <span class="label" part="label">Sort by:</span>
  <div class="select-wrapper" part="select-wrapper">
    <select part="select"></select>
    <span class="arrow" part="arrow">▼</span>
  </div>
</div>
`;

/**
 * Sort Web Component
 * 
 * @class CtlSort
 * @extends HTMLElement
 * 
 * @attr {string} options - JSON array of sort options with label and value
 * @attr {string} value - Currently selected sort value
 * @attr {string} label - Label text (default: "Sort by:")
 * @attr {string} variant - Display variant (compact)
 * @attr {boolean} disabled - Whether the sort is disabled
 * @attr {string} name - Name attribute for form submission
 * 
 * @fires ctl-change - Dispatched when sort selection changes
 * @fires ctl-change.detail.value - The selected sort value
 * @fires ctl-change.detail.label - The selected sort label
 * 
 * @csspart container - The main container
 * @csspart label - The label element
 * @csspart select-wrapper - The select wrapper
 * @csspart select - The select element
 * @csspart arrow - The dropdown arrow
 * 
 * @cssprop --ctl-color-text - Text color
 * @cssprop --ctl-color-muted - Muted text color
 * @cssprop --ctl-color-border - Border color
 * @cssprop --ctl-color-surface - Background color
 * @cssprop --ctl-color-brand - Focus color
 */
export class CtlSort extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    
    this._select = this.shadowRoot.querySelector('select');
    this._label = this.shadowRoot.querySelector('.label');
    this._options = [
      { label: 'Price: Low to High', value: 'price-asc' },
      { label: 'Price: High to Low', value: 'price-desc' },
      { label: 'Size: Small to Large', value: 'size-asc' },
      { label: 'Size: Large to Small', value: 'size-desc' },
      { label: 'Best Value', value: 'best-value' }
    ];
    
    // Bind handler once
    this._boundHandleChange = this._handleChange.bind(this);
  }

  static get observedAttributes() {
    return ['options', 'value', 'label', 'variant', 'disabled', 'name'];
  }

  connectedCallback() {
    this._select.addEventListener('change', this._boundHandleChange);
    this._parseOptions();
    this._render();
  }

  disconnectedCallback() {
    this._select.removeEventListener('change', this._boundHandleChange);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      if (name === 'options') {
        this._parseOptions();
      }
      this._render();
    }
  }

  _parseOptions() {
    const parsed = safeJsonParse(this.getAttribute('options'), []);
    if (Array.isArray(parsed) && parsed.length > 0) {
      this._options = parsed;
    }
  }

  _handleChange(e) {
    const value = e.target.value;
    const option = this._options.find(opt => opt.value === value);
    
    this.setAttribute('value', value);
    
    this.dispatchEvent(new CustomEvent('ctl-change', {
      bubbles: true,
      composed: true,
      detail: { 
        value,
        label: option?.label || value
      }
    }));
  }

  _render() {
    // Label
    const label = this.getAttribute('label') || 'Sort by:';
    this._label.textContent = label;

    // Options
    this._select.innerHTML = '';
    this._options.forEach(option => {
      const opt = document.createElement('option');
      opt.value = option.value;
      opt.textContent = option.label;
      this._select.appendChild(opt);
    });

    // Value
    const value = this.getAttribute('value');
    if (value && this._options.find(opt => opt.value === value)) {
      this._select.value = value;
    } else if (this._options.length > 0) {
      this._select.value = this._options[0].value;
    }

    // Disabled
    this._select.disabled = this.hasAttribute('disabled');

    // Name
    const name = this.getAttribute('name');
    if (name) {
      this._select.name = name;
    }
  }

  /**
   * Get the current sort value
   * @returns {string} The current sort value
   */
  get value() {
    return this._select.value;
  }

  /**
   * Set the sort value
   * @param {string} val - The sort value
   */
  set value(val) {
    this.setAttribute('value', val);
  }

  /**
   * Get the current sort options
   * @returns {Array} The current options array
   */
  get options() {
    return [...this._options];
  }

  /**
   * Set the sort options
   * @param {Array} opts - Array of option objects with label and value
   */
  set options(opts) {
    if (Array.isArray(opts)) {
      this._options = opts;
      this.setAttribute('options', JSON.stringify(opts));
    }
  }

  /**
   * Focus the select element
   */
  focus() {
    this._select.focus();
  }
}

if (!customElements.get("ctl-sort")) {
  customElements.define("ctl-sort", CtlSort);
}
